import { useCallback, useState } from 'react';
import Swal from 'sweetalert2';

// Global SweetAlert configuration
Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

/**
 * Custom hook for API error handling
 * @returns {Object} Error handling utilities
 */
export const useApiErrorHandler = () => {
  const [showError, setShowError] = useState(false);

  const showErrorAlert = useCallback((message, title = 'Error') => {
    Swal.fire({
      icon: 'error',
      title,
      text: message,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'OK',
    });
  }, []);

  const showSuccess = useCallback((message, title = 'Success') => {
    Swal.fire({
      icon: 'success',
      title,
      text: message,
      confirmButtonColor: '#28a745',
      confirmButtonText: 'OK',
    });
  }, []);

  const showWarning = useCallback((message, title = 'Warning') => {
    Swal.fire({
      icon: 'warning',
      title,
      text: message,
      confirmButtonColor: '#ffc107',
      confirmButtonText: 'OK',
    });
  }, []);

  const showInfo = useCallback((message, title = 'Information') => {
    Swal.fire({
      icon: 'info',
      title,
      text: message,
      confirmButtonColor: '#17a2b8',
      confirmButtonText: 'OK',
    });
  }, []);

  const showConfirm = useCallback((message, title = 'Confirm Action') => {
    return Swal.fire({
      icon: 'question',
      title,
      text: message,
      showCancelButton: true,
      confirmButtonColor: '#007bff',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    });
  }, []);

  const handleError = useCallback(
    (error, operation = 'Operation') => {
      let message = 'An unexpected error occurred.';
      let title = `${operation} Failed`;

      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 400:
            message = data.message || 'Invalid request data.';
            break;
          case 401:
            message = 'Authentication required. Please log in again.';
            title = 'Authentication Required';
            // Clear auth data and redirect
            localStorage.removeItem('jwt_token');
            window.location.href = '/login';
            break;
          case 403:
            message = 'You do not have permission to perform this action.';
            title = 'Access Denied';
            break;
          case 404:
            message = 'The requested resource was not found.';
            title = 'Not Found';
            break;
          case 422:
            message =
              data.message || 'Validation failed. Please check your input.';
            title = 'Validation Error';
            break;
          case 500:
            message = 'Server error. Please try again later.';
            title = 'Server Error';
            break;
          default:
            message = data.message || `HTTP ${status} error occurred.`;
        }
      } else if (error.request) {
        message = 'Network error. Please check your connection.';
        title = 'Network Error';
      } else if (error.message) {
        message = error.message;
      }

      showErrorAlert(message, title);
      return message;
    },
    [showErrorAlert]
  );

  const handleValidationErrors = useCallback(
    errors => {
      if (typeof errors === 'object' && errors !== null) {
        const errorMessages = Object.values(errors).flat();
        const message = errorMessages.join('\n');
        showErrorAlert(message, 'Validation Errors');
        return message;
      }
      return null;
    },
    [showErrorAlert]
  );

  const clearAuthData = useCallback(() => {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user');
    sessionStorage.clear();
  }, []);

  return {
    showError: showErrorAlert,
    showSuccess,
    showWarning,
    showInfo,
    showConfirm,
    handleError,
    handleValidationErrors,
    clearAuthData,
  };
};

// Legacy class-based handler for backward compatibility
class ApiErrorHandler {
  static showError(message, title = 'Error') {
    Swal.fire({
      icon: 'error',
      title,
      text: message,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'OK',
    });
  }

  static showSuccess(message, title = 'Success') {
    Swal.fire({
      icon: 'success',
      title,
      text: message,
      confirmButtonColor: '#28a745',
      confirmButtonText: 'OK',
    });
  }

  static showWarning(message, title = 'Warning') {
    Swal.fire({
      icon: 'warning',
      title,
      text: message,
      confirmButtonColor: '#ffc107',
      confirmButtonText: 'OK',
    });
  }

  static showInfo(message, title = 'Information') {
    Swal.fire({
      icon: 'info',
      title,
      text: message,
      confirmButtonColor: '#17a2b8',
      confirmButtonText: 'OK',
    });
  }

  static showConfirm(message, title = 'Confirm Action') {
    return Swal.fire({
      icon: 'question',
      title,
      text: message,
      showCancelButton: true,
      confirmButtonColor: '#007bff',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    });
  }

  static handleError(error, operation = 'Operation') {
    let message = 'An unexpected error occurred.';
    let title = `${operation} Failed`;

    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          message = data.message || 'Invalid request data.';
          break;
        case 401:
          message = 'Authentication required. Please log in again.';
          title = 'Authentication Required';
          localStorage.removeItem('jwt_token');
          window.location.href = '/login';
          break;
        case 403:
          message = 'You do not have permission to perform this action.';
          title = 'Access Denied';
          break;
        case 404:
          message = 'The requested resource was not found.';
          title = 'Not Found';
          break;
        case 422:
          message =
            data.message || 'Validation failed. Please check your input.';
          title = 'Validation Error';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          title = 'Server Error';
          break;
        default:
          message = data.message || `HTTP ${status} error occurred.`;
      }
    } else if (error.request) {
      message = 'Network error. Please check your connection.';
      title = 'Network Error';
    } else if (error.message) {
      message = error.message;
    }

    this.showError(message, title);
    return message;
  }

  static handleValidationErrors(errors) {
    if (typeof errors === 'object' && errors !== null) {
      const errorMessages = Object.values(errors).flat();
      const message = errorMessages.join('\n');
      this.showError(message, 'Validation Errors');
      return message;
    }
    return null;
  }

  static clearAuthData() {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user');
    sessionStorage.clear();
  }
}

export default ApiErrorHandler;
